// Attribution capture: UTMs + ad click IDs + referrer + landing page.
// Snapshot persists for the session so multi-step forms keep first-touch values.

const STORAGE_KEY = "tme_attribution_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid"];
const TRACKED_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS, "referrer", "landing_page"];

const emptyAttribution = () =>
  TRACKED_KEYS.reduce((acc, k) => {
    acc[k] = "";
    return acc;
  }, {});

function readSearchParams() {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
}

function readPersisted() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage?.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persist(snapshot) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* swallow — non-blocking */
  }
}

/**
 * Capture attribution on first visit of the session and persist it.
 * On subsequent calls, the persisted snapshot is preserved (first-touch wins).
 * Should be invoked once at app boot.
 */
export function initAttribution() {
  if (typeof window === "undefined") return emptyAttribution();
  const persisted = readPersisted();
  if (persisted && Object.keys(persisted).length) return persisted;

  const fromUrl = readSearchParams();
  const snapshot = {
    ...emptyAttribution(),
    ...fromUrl,
    referrer: document.referrer || "",
    landing_page: window.location.href || "",
  };
  persist(snapshot);
  return snapshot;
}

/** Return the current persisted attribution snapshot (initialising if needed). */
export function getAttribution() {
  if (typeof window === "undefined") return emptyAttribution();
  return readPersisted() || initAttribution();
}

export { TRACKED_KEYS };
